import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { viaAgentLabel } from "@/lib/via-agent-labels";
import type { Notification } from "../notifications.types";
import { NotificationItemMenu } from "./notification-item-menu";

export function formatRelativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime();
  const diffMins = Math.floor(diffMs / 60_000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

function getNotificationContext(notification: Notification): string {
  const name = notification.container_name ?? "";
  switch (notification.type) {
    case "workspace_invite":
      return `Workspace invite · ${name}`;
    case "project_invite":
    case "project_member_added":
      return `Project invite · ${name}`;
    case "chat_mention":
      return `Chat · ${name}`;
    case "doc_mention":
      return `Document · ${name}`;
    case "comment_mention":
      return `Comment · ${name}`;
    case "project_join_requested":
      return `Join request · ${name}`;
    case "project_join_approved":
      return `Join request approved · ${name}`;
    case "project_join_rejected":
      return `Join request declined · ${name}`;
    case "domain_invite_full":
      return `Domain invite full · ${name}`;
    // CR-263: a message room has no name of its own (its members are its title).
    case "document_shared":
    case "chat_shared":
      if (name) {
        return `Shared "${name}" with you`;
      }
      return notification.type === "document_shared"
        ? "Shared a document with you"
        : "Shared a conversation with you";
  }
}

interface NotificationItemProps {
  notification: Notification;
  onClick: (notification: Notification) => void;
  onMarkRead: (notificationId: string) => void;
  onMarkUnread: (notificationId: string) => void;
  onDelete: (notificationId: string) => void;
}

export function NotificationItem({
  notification,
  onClick,
  onMarkRead,
  onMarkUnread,
  onDelete,
}: NotificationItemProps) {
  const isUnread = !notification.read_at;
  // CR-185: a full domain invite's "actor" is whoever was turned away — the
  // owner is told the invite is full, not who tried (same rule as the email).
  const isDomainInviteFull = notification.type === "domain_invite_full";
  const actorName = isDomainInviteFull
    ? /* @wc-ignore */ "Kairo"
    : (notification.actor_name ?? "Someone");
  // CR-69: undefined for a notification a human produced directly, AND for an
  // agent slug this build does not know — both render as the plain name.
  const viaLabel = isDomainInviteFull ? undefined : viaAgentLabel(notification.via_agent);

  return (
    <div
      className={cn(
        "group flex items-start gap-2 px-3 py-2.5",
        isUnread ? "bg-blue-50/50 hover:bg-blue-50" : "hover:bg-accent",
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className={cn(
              "mt-1.5 flex shrink-0 items-center justify-center rounded-full p-1 transition-colors",
              !isUnread && "opacity-0 group-hover:opacity-100",
            )}
            onClick={(e) => {
              e.stopPropagation();
              if (isUnread) {
                onMarkRead(notification.id);
              } else {
                onMarkUnread(notification.id);
              }
            }}
          >
            <span
              className={cn(
                "h-2 w-2 rounded-full transition-colors",
                isUnread
                  ? "bg-blue-500 hover:bg-blue-600"
                  : "border border-slate-400 bg-transparent",
              )}
            />
          </button>
        </TooltipTrigger>
        <TooltipContent side="left">{isUnread ? "Mark as read" : "Mark as unread"}</TooltipContent>
      </Tooltip>

      <button
        type="button"
        onClick={() => onClick(notification)}
        className="min-w-0 flex-1 cursor-pointer text-left"
      >
        <div className="flex items-baseline justify-between gap-2">
          <span
            data-testid="notification-actor"
            // Exposed for tests: wuchale resolves the copy below to "" under
            // vitest, so the two branches are otherwise indistinguishable.
            // Set only when a label was RESOLVED, so it marks "the via branch
            // rendered" rather than "the column was non-null" — an unrecognised
            // slug renders the plain name and carries no attribute.
            data-via-agent={viaLabel ? notification.via_agent : undefined}
            className={cn("truncate text-sm", isUnread ? "font-semibold" : "font-medium")}
          >
            {/* CR-69: an agent acted for a human — name both, so the bell can
                never claim a person did something they only asked for. */}
            {viaLabel ? (
              <>
                {viaLabel} · via {actorName}
              </>
            ) : (
              actorName
            )}
          </span>
          <span className="shrink-0 text-xs text-muted-foreground">
            {formatRelativeTime(notification.created_at)}
          </span>
        </div>
        <p className="truncate text-xs text-muted-foreground">
          {getNotificationContext(notification)}
        </p>
        {notification.body ? (
          <p className="mt-1 line-clamp-2 text-sm leading-snug text-muted-foreground">
            {notification.body}
          </p>
        ) : null}
      </button>

      <NotificationItemMenu
        isUnread={isUnread}
        notificationId={notification.id}
        onMarkRead={onMarkRead}
        onMarkUnread={onMarkUnread}
        onDelete={onDelete}
      />
    </div>
  );
}
